<template>
  <div class="logwork-approval-container">
    <!-- Header -->
    <div class="approval-header">
      <h2 class="title">{{ $t("logwork.approval.title") }}</h2>

      <!-- Filters -->
      <div class="filter-section">
        <div class="filter-group">
          <a-select
            v-model="selectedWeek"
            :options="weekOptions"
            :placeholder="$t('logwork.approval.select_week')"
            @change="handleWeekChange"
            style="width: 200px"
          />
        </div>
        <div class="date-range">
          {{ weekDateRange }}
        </div>
        <div class="pagination">
          <a-button size="small" icon="left" @click="handlePrevWeek" />
          <a-button size="small" icon="right" @click="handleNextWeek" />
        </div>
      </div>
    </div>

    <!-- Approval Table -->
    <a-spin :spinning="loading">
      <table class="approval-table">
        <thead>
          <tr>
            <th>{{ $t("logwork.approval.week_member") }}</th>
            <th>{{ $t("logwork.approval.time") }}</th>
            <th>{{ $t("logwork.approval.status_label") }}</th>
            <th>{{ $t("logwork.approval.action") }}</th>
          </tr>
        </thead>
        <tbody>
          <!-- Project Groups -->
          <template
            v-for="(members, projectName, projectIndex) in groupedApprovals"
          >
            <!-- Project Header Row -->
            <tr :key="'project-' + projectIndex" class="project-row">
              <td colspan="4" class="project-header">
                <a-icon
                  type="down"
                  :rotate="expandedProjects.includes(projectIndex) ? 0 : -90"
                  @click="toggleProject(projectIndex)"
                  style="cursor: pointer; margin-right: 8px"
                />
                {{ projectName }}
              </td>
            </tr>

            <!-- Member Rows (shown only if project is expanded) -->
            <template v-if="expandedProjects.includes(projectIndex)">
              <tr
                v-for="member in members"
                :key="'member-' + member.memberId"
                class="member-row"
              >
                <td class="member-cell">{{ member.memberName }}</td>
                <td class="time-cell">{{ member.hours }}h</td>
                <td class="status-cell">
                  <a-tag :color="getStatusColor(member.status)">
                    {{ $t("logwork.approval.status." + member.status) }}
                  </a-tag>
                </td>
                <td class="action-cell">
                  <a-button
                    type="primary"
                    size="small"
                    @click="handleApprove(member)"
                    class="approve-btn"
                  >
                    {{ $t("logwork.approval.approve") }}
                  </a-button>
                  <a-button
                    size="small"
                    @click="handleReject(member)"
                    class="reject-btn"
                  >
                    {{ $t("logwork.approval.reject") }}
                  </a-button>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </a-spin>

    <!-- Approval Confirm Modal -->
    <a-modal
      v-model="approvalModalVisible"
      :title="approvalModalTitle"
      :maskClosable="false"
      :footer="null"
      width="500"
      centered
    >
      <div class="approval-confirm-modal">
        <!-- Icon -->
        <div :class="'confirm-icon icon-' + approvalAction">
          <a-icon
            :type="approvalAction === 'approve' ? 'check' : 'close'"
            theme="filled"
          />
        </div>

        <!-- Confirm Message -->
        <div class="confirm-message">
          <p class="confirm-title">{{ approvalConfirmText }}</p>
          <p class="member-info">
            {{ selectedMember ? selectedMember.memberName : "" }}
          </p>
        </div>

        <!-- Note Field (for both approve and reject) -->
        <div class="approval-note">
          <label :class="{ required: approvalAction === 'reject' }">
            {{ $t("logwork.approval.note_label") }}
            <span v-if="approvalAction === 'reject'" class="required-asterisk"
              >*</span
            >
          </label>
          <a-textarea
            v-model="approvalNote"
            :placeholder="$t('logwork.approval.note_placeholder')"
            :rows="3"
          />
        </div>

        <!-- Action Buttons -->
        <div class="approval-actions">
          <a-button @click="handleApprovalCancel">
            {{ $t("logwork.approval.cancel") }}
          </a-button>
          <a-button
            type="primary"
            :danger="approvalAction === 'reject'"
            @click="handleApprovalConfirm"
            :loading="processingApproval"
          >
            {{ $t("logwork.approval.confirm_action") }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import LogworkService from "../../api/logwork";

const service = LogworkService;

export default {
  name: "LogworkApproval",
  data() {
    const today = moment();
    const weekStart = today.clone().startOf("isoWeek");
    return {
      loading: false,
      selectedWeek: null,
      weekOptions: [],
      weekDateRange: "",
      groupedApprovals: {},
      expandedProjects: [0], // First project expanded by default
      approvingMember: null,
      rejectingMember: null,
      approvalModalVisible: false,
      approvalAction: null, // 'approve' or 'reject'
      selectedMember: null,
      approvalNote: "",
      processingApproval: false,
      currentWeekStart: weekStart
    };
  },
  computed: {
    approvalModalTitle() {
      return this.approvalAction === "approve"
        ? this.$t("logwork.approval.modal_approve_title")
        : this.$t("logwork.approval.modal_reject_title");
    },
    approvalConfirmText() {
      return this.approvalAction === "approve"
        ? this.$t("logwork.approval.confirm_approve_text")
        : this.$t("logwork.approval.confirm_reject_text");
    },
    hasNoSubmissions() {
      return Object.keys(this.groupedApprovals).length === 0;
    }
  },
  created() {
    this.initializeWeeks();
    this.fetchApprovals();
  },
  methods: {
    initializeWeeks() {
      // Generate last 8 weeks for selection
      const weeks = [];
      for (let i = 0; i < 8; i++) {
        const weekStart = moment()
          .subtract(i, "weeks")
          .startOf("isoWeek");
        const weekEnd = weekStart.clone().endOf("isoWeek");
        const weekNum = weekStart.isoWeek();
        weeks.push({
          label: `${weekStart.format("MM/DD/YYYY")} ~ ${weekEnd.format(
            "MM/DD/YYYY"
          )}`,
          value: weekNum
        });
      }
      this.weekOptions = weeks;
      // Select current week
      this.selectedWeek = moment().isoWeek();
    },
    handleWeekChange(weekNum) {
      this.selectedWeek = weekNum;
      this.currentWeekStart = moment()
        .isoWeek(weekNum)
        .startOf("isoWeek");
      this.updateWeekDateRange();
      this.fetchApprovals();
    },
    handlePrevWeek() {
      const prevWeek = this.currentWeekStart.clone().subtract(1, "weeks");
      this.selectedWeek = prevWeek.isoWeek();
      this.currentWeekStart = prevWeek.startOf("isoWeek");
      this.updateWeekDateRange();
      this.fetchApprovals();
    },
    handleNextWeek() {
      const nextWeek = this.currentWeekStart.clone().add(1, "weeks");
      this.selectedWeek = nextWeek.isoWeek();
      this.currentWeekStart = nextWeek.startOf("isoWeek");
      this.updateWeekDateRange();
      this.fetchApprovals();
    },
    updateWeekDateRange() {
      const start = this.currentWeekStart.format("MMMM DD");
      const end = this.currentWeekStart
        .clone()
        .endOf("isoWeek")
        .format("MMMM DD");
      this.weekDateRange = `${start} - ${end}`;
    },
    fetchApprovals() {
      this.loading = true;
      service
        .getApprovalList({
          week_number: this.selectedWeek
        })
        .then(response => {
          const approvals = response.data?.data || [];
          this.groupedApprovals = this.groupApprovalsByProjectAndMember(
            approvals
          );
        })
        .catch(error => {
          console.error("Failed to fetch approvals:", error);
          this.$message.error(this.$t("logwork.approval.error_loading"));
          this.groupedApprovals = {};
        })
        .finally(() => {
          this.loading = false;
        });
    },
    groupApprovalsByProjectAndMember(approvals) {
      const grouped = {};
      approvals.forEach(approval => {
        const projectName = approval.projectName || "Unknown Project";
        if (!grouped[projectName]) {
          grouped[projectName] = [];
        }
        grouped[projectName].push(approval);
      });
      return grouped;
    },
    toggleProject(projectIndex) {
      if (this.expandedProjects.includes(projectIndex)) {
        this.expandedProjects = this.expandedProjects.filter(
          i => i !== projectIndex
        );
      } else {
        this.expandedProjects.push(projectIndex);
      }
    },
    getStatusColor(status) {
      const colors = {
        submitted: "green",
        resubmitted: "magenta",
        rejected: "gray",
        approved: "blue"
      };
      return colors[status] || "default";
    },
    handleApprove(member) {
      this.selectedMember = member;
      this.approvalAction = "approve";
      this.approvalNote = "";
      this.approvalModalVisible = true;
    },
    handleReject(member) {
      this.selectedMember = member;
      this.approvalAction = "reject";
      this.approvalNote = "";
      this.approvalModalVisible = true;
    },
    handleApprovalCancel() {
      this.approvalModalVisible = false;
      this.selectedMember = null;
      this.approvalAction = null;
      this.approvalNote = "";
    },
    handleApprovalConfirm() {
      if (this.approvalAction === "reject" && !this.approvalNote.trim()) {
        this.$message.warning(this.$t("logwork.approval.note_required"));
        return;
      }

      this.processingApproval = true;
      const endpoint =
        this.approvalAction === "approve"
          ? service.approveLogwork
          : service.rejectLogwork;

      endpoint({
        member_id: this.selectedMember.memberId,
        week_number: this.selectedWeek,
        note: this.approvalNote
      })
        .then(() => {
          const message =
            this.approvalAction === "approve"
              ? this.$t("logwork.approval.approve_success")
              : this.$t("logwork.approval.reject_success");
          this.$message.success(message);
          this.approvalModalVisible = false;
          this.fetchApprovals();
        })
        .catch(error => {
          console.error(`${this.approvalAction} failed:`, error);
          const message =
            this.approvalAction === "approve"
              ? this.$t("logwork.approval.approve_error")
              : this.$t("logwork.approval.reject_error");
          this.$message.error(message);
        })
        .finally(() => {
          this.processingApproval = false;
          this.selectedMember = null;
          this.approvalAction = null;
        });
    }
  }
};
</script>

<style scoped>
.logwork-approval-container {
  padding: 24px;
  background: #fff;
}

.approval-header {
  margin-bottom: 24px;
}

.title {
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
}

.filter-group {
  flex: 0 0 auto;
}

.date-range {
  flex: 1;
  text-align: center;
  font-weight: 500;
  color: #262626;
}

.pagination {
  display: flex;
  gap: 8px;
}

.approval-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
}

.approval-table thead {
  background-color: #1890ff;
}

.approval-table thead th {
  color: white;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border: 1px solid #1890ff;
}

.approval-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.approval-table tbody tr:hover {
  background-color: #fafafa;
}

.approval-table tbody td {
  padding: 12px;
  border: 1px solid #f0f0f0;
}

.project-row {
  background-color: #e6f7ff;
}

.project-header {
  font-weight: 600;
  color: #262626;
  cursor: pointer;
  user-select: none;
}

.member-row {
  background-color: #ffffff;
}

.member-cell {
  padding-left: 32px;
  font-weight: 500;
}

.time-cell {
  text-align: center;
  font-weight: 500;
}

.status-cell {
  text-align: center;
}

.action-cell {
  text-align: center;
}

.approve-btn {
  margin-right: 8px;
}

.reject-btn {
  margin-left: 0;
}

.empty-state {
  text-align: center;
  padding: 40px 12px;
  color: #8c8c8c;
}

.text-muted {
  color: #8c8c8c;
}

/* Approval Confirm Modal */
.approval-confirm-modal {
  text-align: center;
}

.confirm-icon {
  margin-bottom: 24px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.confirm-icon.icon-approve {
  background-color: #f6ffed;
}

.confirm-icon.icon-reject {
  background-color: #fff1f0;
}

.confirm-icon /deep/ .anticon {
  font-size: 32px;
}

.confirm-icon.icon-approve /deep/ .anticon {
  color: #52c41a;
}

.confirm-icon.icon-reject /deep/ .anticon {
  color: #ff4d4f;
}

.confirm-message {
  margin-bottom: 24px;
}

.confirm-message .confirm-title {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #595959;
  font-weight: 400;
  line-height: 1.5;
}

.confirm-message .member-info {
  font-weight: 600;
  font-size: 16px;
  margin: 0;
  color: #262626;
}

.approval-note {
  margin-bottom: 24px;
  text-align: left;
}

.approval-note label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #262626;
}

.approval-note label.required .required-asterisk {
  color: #ff4d4f;
  margin-left: 4px;
}

.approval-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.approval-actions button {
  min-width: 120px;
}
</style>
